import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import Grid from '@mui/material/Grid';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import {
  API,
  MimeTypeHelper,
  MediaFormatHelper,
  Field2 as Field,
  VibeIcon,
  viEdit,
  viAdd,
  viAddCircle,
  color,
  VibeCheckbox,
  VibeModal,
} from 'vibeguide';
import defaultLocationImage from '../../../assets/default_location.png';
import './Information.scss';

class Information extends PureComponent {
  constructor(props) {
    super(props);

    this.locationZone = null;

    this.state = {
      showOnToggleModal: false,
      showOffToggleModal: false,
      localTestLocation: false,
    };
  }

  /**
   * When a field is changed
   */
  onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    const {
      onUpdate,
    } = this.props;

    const includeData = {};

    // when media format is changed (and a location zone is selected) choose the right impression multiplier to use
    if (name === 'mediaFormat' && this.locationZone) {
      const useImpressionMultiplier = get(this.locationZone, `${value}Multiplier`) > 0
        ? get(this.locationZone, `${value}Multiplier`)
        : 1;

      // set impression multiplier when the zone is changed (assume 100% if none is set on the location zone)
      includeData.impressionMultiplier = this.locationZone
        ? useImpressionMultiplier ?? 1
        : 0;
    }

    onUpdate({
      [name]: value,
      ...includeData,
    });
  };

  /**
   * When the impression multiplier is changed
   */
  onChangeImpressionMultiplier = (e) => {
    const {
      onUpdate,
    } = this.props;

    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: toNumber(value) / 100,
    });
  };

  /**
   * When a dropdown is changed
   */
  onChangeDropdown = ({
    id,
    name,
    value,
    data,
  }) => {
    const {
      mediaFormat,
      onUpdate,
    } = this.props;

    const includeData = {};

    if (name === 'zoneName') {
      // include zone ID if changing the location zone
      includeData.zoneId = id;

      // save location zone
      this.locationZone = data;

      const useImpressionMultiplier = get(this.locationZone, `${mediaFormat}Multiplier`) > 0
        ? get(this.locationZone, `${mediaFormat}Multiplier`)
        : 1;

      // set impression multiplier when the zone is changed (assume 100% if none is set on the location zone)
      includeData.impressionMultiplier = this.locationZone
        ? useImpressionMultiplier ?? 1
        : 0;
    } else if (name === 'bannerName') {
      // include banner ID if changing the location banner
      includeData.bannerId = id;

      // remove the location zone / impression multiplier when the banner is changed
      includeData.zoneId = '';
      includeData.zoneName = '';
      includeData.impressionMultiplier = 0;
      // VW-7747: clear the cached zone so a later Media Format change
      // can't pull back the old banner's zone multiplier.
      this.locationZone = null;
    }

    onUpdate({
      [name]: value,
      ...includeData,
    });
  };

  /**
   * When the tag field is changed
   */
  onChangeTag = (data) => {
    const {
      onUpdate,
    } = this.props;

    onUpdate(data);
  };

  /**
   * When the company field is changed
   */
  onSetCompany = async ({
    id: companyId,
    value: companyName,
  }) => {
    const {
      companyId: currCompanyId,
      category,
      tags,
      user,
      onUpdate,
    } = this.props;

    const companyChanged = companyId !== currCompanyId;

    const data = !companyId
      ? {
        tags: currCompanyId !== companyId
          ? tags.filter(tag => tag.type === 'admin')
          : tags,
      }
      : {};

    // VW-7746: when the Company changes, the previously selected banner
    // belongs to the old company and the API would either reject or
    // misfile the resulting location. Reset the banner / zone / multiplier
    // so the user picks fresh values from the new company's banners.
    const resetBannerAndZone = companyChanged
      ? {
        bannerId: '',
        bannerName: '',
        zoneId: '',
        zoneName: '',
        impressionMultiplier: 0,
      }
      : {};

    if (companyChanged) {
      this.locationZone = null;
    }

    onUpdate({
      companyId,
      companyName,
      ...data,
      ...resetBannerAndZone,
    });

    // populate the category if not already set
    if (!category && user.can('company.view')) {
      const company = await API.Company.getById(companyId);

      onUpdate({
        category: company.category || '',
      });
    }
  };

  onDrop = (acceptedFiles) => {
    const file = get(acceptedFiles, '[0]', false);

    if (file) {
      const {
        onUpdate,
        onUploadImage,
      } = this.props;

      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        // Save image data to state to show as a preview
        onUpdate({
          imageUrl: fileAsDataURL,
        });
      };

      reader.onabort = () => console.error('file reading was aborted');
      reader.onerror = () => console.error('file reading has failed');

      reader.readAsDataURL(file);

      onUploadImage(file);
    }
  };

  confirmToggleTestLocation = () => {
    const {
      onUpdate,
    } = this.props;

    const {
      localTestLocation,
      showOffToggleModal,
    } = this.state;

    if (showOffToggleModal) {
      this.setState({
        showOffToggleModal: false,
      });
    } else {
      this.setState({
        showOnToggleModal: false,
      });
    }

    onUpdate({
      testLocation: localTestLocation,
    });
  };

  closeOnToggleTestLocationModal = () => {
    this.setState({
      showOnToggleModal: false,
    });
  };

  closeOffToggleTestLocationModal = () => {
    this.setState({
      showOffToggleModal: false,
    });
  };

  onToggleTestLocation = (e) => {
    const {
      checked,
    } = e;

    const {
      localTestLocation,
    } = this.state;

    if (!localTestLocation) {
      this.setState({
        showOnToggleModal: true,
        localTestLocation: checked,
      });
    } else {
      this.setState({
        showOffToggleModal: true,
        localTestLocation: checked,
      });
    }
  };

  render() {
    const {
      name,
      image: locationImage,
      companyName,
      companyId,
      bannerId,
      bannerName,
      category,
      salesforceId,
      testLocation,
      mediaFormat,
      zoneId,
      zoneName,
      impressionMultiplier,
      contentRating,
      tags,
      disableInput,
      user,
      isNew,
      disableBannerField,
    } = this.props;

    const {
      showOnToggleModal,
      showOffToggleModal,
    } = this.state;

    const canAdminEdit = user.sysAdmin && !disableInput;
    const canSetCompany = user.sysAdmin;
    const showContentRating = mediaFormat !== 'visual';

    return (
      <div className="Information location">
        <div className="data-row">
          <div className="location-image">
            <Dropzone
              accept={MimeTypeHelper.images}
              multiple={false}
              onDrop={this.onDrop}
            >
              {({ getRootProps, getInputProps, isDragActive }) => {
                return (
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'dropzone--isActive' : ''}`}
                  >
                    <input {...getInputProps()} />

                    {locationImage ? (
                      <img src={locationImage} alt="Location Preview" />
                    ) : (
                      <img src={defaultLocationImage} alt="Location" />
                    )}

                    {isDragActive ? (
                      <div className="icon-drop">
                        <VibeIcon
                          className="icon"
                          icon={viAddCircle}
                          color={color.white}
                          size={40}
                        />
                      </div>
                    ) : null}

                    <div className="icon-container">
                      {isDragActive ? (
                        <VibeIcon
                          type="button"
                          icon={viAdd}
                          color={color.white}
                          size={16}
                          buttonProps={{
                            size: 24,
                            color: color.primary,
                          }}
                        />
                      ) : (
                        <VibeIcon
                          type="button"
                          icon={viEdit}
                          color={color.white}
                          size={16}
                          buttonProps={{
                            size: 24,
                            color: color.primary,
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              }}
            </Dropzone>
          </div>

          <div>
            <Grid spacing={2} container>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="text"
                  label="Name"
                  name="name"
                  placeholder="Name"
                  value={name}
                  tabIndex={20}
                  disabled={disableInput}
                  testId="location-name-input"
                  onChange={this.onChange}
                  required
                  autoFocus
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="company"
                  label="Company"
                  name="companyName"
                  value={companyName}
                  tabIndex={21}
                  companyProps={{
                    companyId,
                    companyName,
                  }}
                  disabled={disableInput || !canSetCompany || !isNew}
                  testId="location-company-input"
                  onChange={this.onSetCompany}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="dropdown"
                  label="Category"
                  name="category"
                  value={category}
                  tabIndex={22}
                  dropdownProps={{
                    type: 'category',
                    attr: 'value',
                    fetch: API.Company.Category.list,
                    create: API.Company.Category.create,
                    canCreate: user.can('company_category.create'),
                    successMessage: 'COMPANYCATEGORY.CREATED',
                  }}
                  disabled={disableInput}
                  testId="location-category-dropdown"
                  onChange={this.onChangeDropdown}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  // VW-7746: Remount when the Company changes so the
                  // DropdownSelector drops the previous company's cached
                  // banner list instead of serving stale options.
                  key={`location-banner-${companyId || 'empty'}`}
                  type="dropdown"
                  label="Location Banner"
                  name="bannerName"
                  value={bannerName}
                  tabIndex={23}
                  dropdownProps={{
                    type: 'banner',
                    attr: 'value',
                    companyId,
                    fetch: API.Company.Banner.list,
                  }}
                  disabled={!canAdminEdit || disableBannerField || !companyId}
                  testId="location-banner-dropdown"
                  onChange={this.onChangeDropdown}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="text"
                  label="Salesforce ID"
                  name="salesforceId"
                  placeholder="XXXXXXXXXXXXXXXXXX"
                  value={salesforceId}
                  tabIndex={24}
                  disabled={!canAdminEdit}
                  testId="location-salesforce-id-input"
                  onChange={this.onChange}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }} sx={{ '& .toggle-container': { marginTop: { lg: '20px' } } }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  className="toggle-container"
                >
                  <VibeCheckbox
                    label="Test Location"
                    name="testLocation"
                    color={color.primary}
                    checked={testLocation}
                    testId="location-test-location-checkbox"
                    onChange={this.onToggleTestLocation}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="data-row">
          <div className="data-fields">
            <Grid spacing={2} container>
              <Grid size={6}>
                <Field
                  type="select"
                  label="Media Format"
                  name="mediaFormat"
                  value={mediaFormat}
                  options={MediaFormatHelper.list({ includeEmpty: true })}
                  disabled={!isNew || !canAdminEdit}
                  tabIndex={25}
                  testId="location-media-format-select"
                  onChange={this.onChange}
                  required={isNew}
                />
              </Grid>

              {showContentRating && (
                <Grid size={6}>
                  <Field
                    type="select"
                    label="Max Allowed Content Rating"
                    name="contentRating"
                    value={contentRating}
                    options={[
                      {
                        label: '',
                        value: '',
                      },
                      {
                        label: 'Radio Edit',
                        value: 'radio-edit',
                      },
                      {
                        label: 'G',
                        value: 'g',
                      },
                      {
                        label: 'Explicit',
                        value: 'explicit',
                      },
                    ]}
                    tabIndex={26}
                    disabled={disableInput}
                    testId="location-content-rating-select"
                    onChange={this.onChange}
                    required
                  />
                </Grid>
              )}
            </Grid>
          </div>
        </div>

        <div className="data-row">
          <div className="data-fields">
            <Grid spacing={2} container>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  // VW-7747: Remount when the Banner changes so the
                  // DropdownSelector re-fetches the new banner's zones
                  // instead of showing the previous banner's cached list.
                  key={`location-zone-${bannerId || 'empty'}`}
                  type="dropdown"
                  label="Location Zone"
                  placeholder="Search for a Location Zone..."
                  name="zoneName"
                  value={zoneName}
                  dropdownProps={{
                    type: 'location zone',
                    attr: 'name',
                    companyId,
                    noItemsText: 'No location zones are assigned to this banner',
                    // Look up zones for the selected banner from the API,
                    // same banner-scoped pattern as VW-5352.
                    fetch: async () => {
                      if (!bannerId || !companyId) return [];
                      const response = await API.Company.Banner.get({
                        _id: bannerId,
                        companyId,
                      });
                      return get(response, 'locationZones', []);
                    },
                  }}
                  disabled={!canAdminEdit || !bannerId || !mediaFormat}
                  testId="location-zone-dropdown"
                  onChange={this.onChangeDropdown}
                  tabIndex={27}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <Field
                  type="number"
                  label="Impression Multiplier"
                  name="impressionMultiplier"
                  placeholder="Enter value between 1-100"
                  value={impressionMultiplier || ''}
                  tabIndex={28}
                  numberProps={{
                    min: 0,
                    max: 100,
                    inputGroupRight: '%',
                  }}
                  disabled={!canAdminEdit || !zoneId}
                  testId="location-impression-multiplier-input"
                  onChange={this.onChangeImpressionMultiplier}
                />
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="data-row">
          <div className="data-fields">
            <Field
              type="tags"
              label="Tags"
              name="tags"
              tagProps={{
                companyId,
                tags,
                allowTypes: ['client', 'admin'],
                showAs: 'list',
                assign: user.can('location.assign_tag'),
              }}
              disabled={disableInput}
              testId="location-tags-input"
              onChange={this.onChangeTag}
              tabIndex={29}
            />
          </div>
        </div>

        <VibeModal
          show={showOnToggleModal}
          type="confirm"
          title="Test Location"
          text="You are marking this as a test location, excluding it from reporting."
          testIds={{
            root: 'location-test-location-on-dialog',
            confirm: 'location-test-location-on-confirm-button',
            cancel: 'location-test-location-on-cancel-button',
          }}
          onConfirm={this.confirmToggleTestLocation}
          onClose={this.closeOnToggleTestLocationModal}
        />

        <VibeModal
          show={showOffToggleModal}
          type="confirm"
          title="Test Location"
          text="You are removing this as a test location. This location will now show in reporting."
          testIds={{
            root: 'location-test-location-off-dialog',
            confirm: 'location-test-location-off-confirm-button',
            cancel: 'location-test-location-off-cancel-button',
          }}
          onConfirm={this.confirmToggleTestLocation}
          onClose={this.closeOffToggleTestLocationModal}
        />
      </div>
    );
  }
}

Information.propTypes = {
  name: PropTypes.string,
  companyName: PropTypes.string,
  companyId: PropTypes.string,
  bannerId: PropTypes.string,
  bannerName: PropTypes.string,
  category: PropTypes.string,
  testLocation: PropTypes.bool,
  mediaFormat: PropTypes.string,
  zoneId: PropTypes.string,
  zoneName: PropTypes.string,
  impressionMultiplier: PropTypes.number,
  contentRating: PropTypes.string,
  image: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.object),
  disableInput: PropTypes.bool,
  disableBannerField: PropTypes.bool,
  isNew: PropTypes.bool,
  onUploadImage: PropTypes.func,
  onUpdate: PropTypes.func,
};

Information.defaultProps = {
  name: '',
  companyName: '',
  companyId: '',
  bannerId: '',
  bannerName: '',
  category: '',
  testLocation: false,
  mediaFormat: '',
  zoneId: '',
  zoneName: '',
  impressionMultiplier: null,
  contentRating: '',
  image: null,
  tags: [],
  disableInput: false,
  disableBannerField: false,
  isNew: false,
  onUploadImage: () => {},
  onUpdate: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(Information);
